import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { UseTopPlayers } from "../../hooks/kpi";
import { formatKpiPercent } from "../../helpers/scoreAggregate";

/**
 * Standing — everyone's number for ONE metric, ranked.
 *
 * This is one of the two things that sit behind every metric in the system (the
 * other being the individual records that add up to it), which is why it works
 * on any custom component and not just the self-generated card it was drawn for.
 *
 * It reads the same source the leaderboard does — UseTopPlayers, whose per-player
 * `scores[kpiId].amt` is the raw amount — so no new endpoint and no new shape.
 *
 * A plain ordered list, 1 2 3 4, that scrolls. An earlier version showed a window
 * (leader, then you and your neighbours, with a break between) which read as
 * missing data rather than a deliberate skip: ranks that jump look broken. The
 * full list is longer but it is honest, and the scroll keeps the card height
 * fixed whether the team is eight people or sixty.
 */

const fmt = (kind, n, meta) => {
  const v = Number(n) || 0;
  if (kind === "$") return `$${Math.round(v).toLocaleString()}`;
  if (kind === "%") {
    const pct = Math.abs(v) <= 1 ? v : v / 100;
    return formatKpiPercent(meta, pct);
  }
  return Math.round(v).toLocaleString();
};

const Standing = ({ metricId, metricTitle, kind = "number", period = "month", primary = "#1B598E" }) => {
  const metricMeta = { title: metricTitle };
  const players = UseTopPlayers(period);
  const listRef = useRef(null);
  const meRef = useRef(null);

  const rows = useMemo(() => {
    const src = Array.isArray(players) ? players : [];
    return src
      // The Yeti is a pacing device, not a colleague — it never ranks against
      // the team. Players with no entry for this metric are left out rather
      // than shown as zero, which would read as "they did nothing" when the
      // truth is the metric does not apply to them.
      .filter((p) => p && !p.isYeti && p.scores && p.scores[metricId] != null)
      .map((p) => ({
        id: p.userId,
        name: [p.firstName, p.lastName].filter(Boolean).join(" ") || "Player",
        amt: Number(p.scores?.[metricId]?.amt) || 0,
        me: !!p.ownId,
      }))
      .sort((a, b) => b.amt - a.amt);
  }, [players, metricId]);

  /* Open on the viewing player's row. Without this a rep sitting 9th sees the
     top of a list they are not on and has to hunt for themselves. */
  useEffect(() => {
    const box = listRef.current;
    const me = meRef.current;
    if (!box || !me) return;
    const top = me.offsetTop - box.clientHeight / 2 + me.clientHeight / 2;
    box.scrollTop = Math.max(0, top);
  }, [rows.length]);

  if (!rows.length) {
    return (
      <p style={{ margin: 0, fontSize: 11.5, color: "var(--tvd-muted, #98A2B3)", lineHeight: 1.6 }}>
        No one has a score for {metricTitle || "this metric"} yet. The moment they do, the ranking fills in.
      </p>
    );
  }

  const meIdx = rows.findIndex((r) => r.me);
  const ahead = meIdx > 0 ? rows[meIdx - 1] : null;
  const gap = ahead ? ahead.amt - rows[meIdx].amt : 0;

  return (
    <>
      <div
        ref={listRef}
        className="gmbw-standing"
        /* Four rows, then scroll. The block must never be the thing that sets
           the card's height — the row it sits in does that. */
        style={{ maxHeight: 132, overflowY: "auto", paddingRight: 4, marginRight: -4 }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {rows.map((r, i) => (
            <div
              key={r.id || `${r.name}${i}`}
              ref={r.me ? meRef : null}
              style={{
                display: "flex", alignItems: "center", gap: 11,
                padding: "7px 10px", borderRadius: 8, fontSize: 11.5,
                background: r.me ? "var(--tvd-tile, #F4F8FC)" : "transparent",
                border: r.me ? "1px solid var(--tvd-hair, #DFEAF4)" : "1px solid transparent",
              }}
            >
              <span style={{
                width: 16, flex: "none", textAlign: "right", fontSize: 10.5,
                fontVariantNumeric: "tabular-nums",
                color: r.me ? primary : "var(--tvd-muted, #98A2B3)", fontWeight: r.me ? 600 : 400,
              }}>{i + 1}</span>
              <span style={{
                flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                color: r.me ? "var(--tvd-ink, #0F172A)" : "var(--tvd-body, #475467)", fontWeight: r.me ? 600 : 400,
              }}>{r.name}</span>
              <span style={{
                fontVariantNumeric: "tabular-nums",
                color: r.me ? "var(--tvd-ink, #0F172A)" : "var(--tvd-ink, #344054)", fontWeight: r.me ? 600 : 500,
              }}>{fmt(kind, r.amt, metricMeta)}</span>
            </div>
          ))}
        </div>
      </div>
      {ahead && gap > 0 && (
        <div style={{ fontSize: 10.5, color: "var(--tvd-muted, #98A2B3)", marginTop: 10, paddingTop: 9, borderTop: "1px solid var(--tvd-hair, #F4F6F9)" }}>
          <b style={{ color: primary, fontWeight: 600 }}>{fmt(kind, gap, metricMeta)}</b> to pass {ahead.name}
        </div>
      )}
    </>
  );
};

Standing.propTypes = {
  metricId: PropTypes.string,
  metricTitle: PropTypes.string,
  kind: PropTypes.string,
  period: PropTypes.string,
  primary: PropTypes.string,
};

Standing.defaultProps = {
  metricId: null,
  metricTitle: "",
  kind: "number",
  period: "month",
  primary: "#1B598E",
};

export default Standing;
